/// <amd-module name="Core/Medius.Core.Web/Scripts/components/taskHandling/shared/modals/shared/reasonCodesGrid"/>
import * as React from "react";
import { AutoComplete, ColumnDefinitionSet, DataTable, FlexContent, ImageSettingsSearcher, Text } from "@medius/ui-controls";
import { translate } from "Core/Medius.Core.Web/Scripts/lib/globalization";
import { IReasonCode, IInvoiceReasonCodeData } from "../rejectionManagementWizard/interfaces";
import { ReasonCodeOperationType } from "./interfaces";

interface ReasonCodesGridProps {
    selection: IReasonCode[];
    setSelection: React.Dispatch<React.SetStateAction<IReasonCode[]>>;
    invoiceReasonCodeData: IInvoiceReasonCodeData;
    reasonCodeOperationType?: ReasonCodeOperationType;
}

export default function ReasonCodesGrid({ selection, setSelection, invoiceReasonCodeData, reasonCodeOperationType }: ReasonCodesGridProps) {
    const {
        invoiceReasonCodes,
        totalInvoiceReasonCodeCount,
        actions,
        isLoading,
        isError,
        paging,
        sorting
    } = invoiceReasonCodeData;

    const selectionRef = React.useRef(selection);
    selectionRef.current = selection;

    const handleSelectedRowsChanged = React.useCallback((ids: string[]) => {
        if (!ids || ids.length === 0) {
            setSelection([]);
            return;
        }

        setSelection(prevSelection => {
            const newlySelected = invoiceReasonCodes.filter(rc => ids.includes(String(rc.id)));

            const stillSelected = prevSelection.filter(
                rc => !invoiceReasonCodes.some(pageRc => pageRc.id === rc.id) || ids.includes(String(rc.id))
            );

            const merged = [...stillSelected, ...newlySelected];
            return merged.filter((item, idx, arr) => arr.findIndex(i => i.id === item.id) === idx);
        });
    }, [invoiceReasonCodes, setSelection]);

    const handleSelectedActionOnRowChanged = React.useCallback((rowItem: IReasonCode, value: string) => {
        setSelection((prevSelection: IReasonCode[]) => {
            const updatedSelection = prevSelection.map(selected => {
                if (selected.id === rowItem.id) {
                    const updated = {
                        ...selected,
                        rejectionActionId: value !== null ? Number(value) : null
                    };
                    return updated;
                }
                return selected;
            });

            return updatedSelection;
        });
    }, [setSelection]);

    const transformTypeToTranslationKey = (input: string) => {
        return translate('#PurchaseToPay/documentReasonCodes/' + input.toLocaleLowerCase());
    };

    const baseColumns: ColumnDefinitionSet<IReasonCode> = React.useMemo(() => [{
        columnType: 'customContent',
        displayName: 
            reasonCodeOperationType === ReasonCodeOperationType.PutOnHold ?
                translate("#PurchaseToPay/documentReasonCodes/reasonCode") : 
                translate("#PurchaseToPay/documentReasonCodes/code"),
        enableSorting: true,
        maxColumnSize: "content",
        key: 'reasonCode',
        accessorFn: (rowItem) => rowItem.reasonCode,
        renderRowContent(rowItem) {
            return (
                <FlexContent maxWidth={2}><Text variant="body" maxLines={12}>{rowItem.reasonCode}</Text></FlexContent>
            );
        },
        overflowType: 'callout'
    },
    {
      columnType: 'customContent',
        displayName: translate("#Core/description"),
        enableSorting: true,
        key: 'description',
        accessorFn: (rowItem) => rowItem.description,
        renderRowContent(rowItem) {
            return (
                <FlexContent maxWidth={4}><Text variant="body" maxLines={12}>{rowItem.description}</Text></FlexContent>
            );
        },
        overflowType: 'callout'
    },
    {
        columnType: 'customContent',
        displayName: translate("#Core/type"),
        enableSorting: true,
        key: 'types',
        accessorFn: (rowItem) => rowItem.types,
        renderRowContent(rowItem) {
            return (
                <FlexContent maxWidth={2}>{rowItem.types.map(type => transformTypeToTranslationKey(type)).join(", ")}</FlexContent>
            );
        },
        overflowType: 'callout'
    }], [reasonCodeOperationType]);

    const rejectionActionsColumn: ColumnDefinitionSet<IReasonCode> = React.useMemo(() => [{
        columnType: 'customContent',
        displayName: translate('#PurchaseToPay/action'),
        enableSorting: true,
        key: 'rejectionActions',
        accessorFn: (rowItem: IReasonCode) => rowItem.rejectionActionId,
        renderRowContent(rowItem: IReasonCode) {
            const selectedItem = selectionRef.current.find(selected => selected.id === rowItem.id);
            return (
                selectedItem
                    ? <AutoComplete allItems={actions}
                        name="rejection-action-autocomplete"
                        data-testid="reason-codes-grid-rejection-action-autocomplete"
                        value={selectedItem.rejectionActionId?.toString()}
                        useStringValue
                        height="small"
                        onChange={(value) => handleSelectedActionOnRowChanged(rowItem, value.value)} />
                    : <></>
            );
        },
    }], [actions, handleSelectedActionOnRowChanged]);

    const ColReasonCodeEntry: ColumnDefinitionSet<IReasonCode> = React.useMemo(() => {
        if (actions.length > 0 && selection.length > 0) {
            return [...baseColumns, ...rejectionActionsColumn];
        }
        return baseColumns;
    }, [baseColumns, rejectionActionsColumn, actions.length, selection]);


    const dataTableKey = React.useMemo(() => {
        return `datatable-cols-${ColReasonCodeEntry.length}-sel-${selection.length}`;
    }, [ColReasonCodeEntry.length, selection]);

    return (
        <>
            <DataTable<IReasonCode>
                key={dataTableKey}
                columns={ColReasonCodeEntry}
                getRowId={(item: IReasonCode) => String(item.id)}
                rowSelection={{
                    allowMultiple: true,
                    variant: "column",
                    onSelectedRowsChanged: handleSelectedRowsChanged,
                    selectedRows: selection.map(rc => String(rc.id))
                }}
                dataMethod={"controlled"}
                isContainerFixed={true}
                isLoading={isLoading}
                currentPageData={invoiceReasonCodes}
                totalRows={totalInvoiceReasonCodeCount}
                loadError={isError}
                label="Data table"
                enableResizing={false}
                variant="standard"
                data-testid="reason-codes-grid"
                maxLinesPerRow={4}
                paging={{
                    availablePageSizes: [25, 50, 100],
                    currentPageIndex: paging.currentPage,
                    currentPageSize: paging.pageSize,
                    onPageSizeChanged: paging.changePageSize,
                    onPageChanged: paging.changePage
                }}
                sorting={{
                    enableSorting: true,
                    currentSort: sorting.sortInfo,
                    onSortChanged: sorting.changeSortInfo
                }}
                resultMessage={ 
                    reasonCodeOperationType === ReasonCodeOperationType.PutOnHold ?
                    {
                        title: translate("#Core/NoMatchesFound"),
                        explanation: translate("#PurchaseToPay/reasonCodeOperationModal/noMatchesFoundDescription"),
                        image: <ImageSettingsSearcher width={150} />
                    } :
                    {
                        title: translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/noReasonCodeFound"),
                        explanation: translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/noReasonCodeFoundExplanation"),
                        image: <ImageSettingsSearcher width={120} />
                }}
            />
        </>
    );
}